/**
 * PBX node management API (brief §3.2/§3.3): node CRUD, the platform
 * service-account credential, webcdr token rotation, and a live
 * connection test. Connection changes take effect for the dashboard and
 * collectors at the next backend restart (the API repeats this note).
 */

import { apiFetch } from "./client";

/** A PBX node row (no secrets, no token hash). */
export interface PbxNodeRow {
  id: number;
  name: string;
  base_url: string;
  ws_url: string | null;
  verify_tls: boolean;
  routing_prefix: string | null;
  inter_node_trunk: string | null;
  status: string;
  service_account_user: string | null;
  has_webcdr_token: boolean;
}

export type PbxNodeIn = Partial<
  Pick<
    PbxNodeRow,
    "name" | "base_url" | "ws_url" | "verify_tls" | "routing_prefix" | "inter_node_trunk"
  >
>;

/** The one-time webcdr rotation result — the raw token is never shown again. */
export interface WebcdrRotation {
  node_id: number;
  webcdr_token: string;
  cdr_adr: string;
}

/** The connection-test outcome. */
export interface NodeTestResult {
  ok: boolean;
  reason?: string;
  version?: string | null;
  note?: string;
}

export function getNodesConfig(): Promise<{ items: PbxNodeRow[] }> {
  return apiFetch("/api/instance/nodes");
}

export function createNode(body: PbxNodeIn): Promise<PbxNodeRow & { note: string }> {
  return apiFetch("/api/instance/nodes", { method: "POST", body });
}

export function updateNode(
  id: number,
  body: PbxNodeIn,
): Promise<PbxNodeRow & { note: string }> {
  return apiFetch(`/api/instance/nodes/${id}`, { method: "PUT", body });
}

export function deleteNode(id: number): Promise<{ deleted: number }> {
  return apiFetch(`/api/instance/nodes/${id}`, { method: "DELETE" });
}

/** Set/replace the platform service account (protected permission). */
export function setNodeServiceAccount(
  id: number,
  body: { username: string; password: string },
): Promise<PbxNodeRow & { note: string }> {
  return apiFetch(`/api/instance/nodes/${id}/service-account`, { method: "PUT", body });
}

/** Rotate the webcdr push token; the raw token is returned once. */
export function rotateNodeWebcdrToken(id: number): Promise<WebcdrRotation> {
  return apiFetch(`/api/instance/nodes/${id}/webcdr-token`, { method: "POST" });
}

/** Try a real login against the node with its stored credential. */
export function testNode(id: number): Promise<NodeTestResult> {
  return apiFetch(`/api/instance/nodes/${id}/test`, { method: "POST" });
}
